import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Modal, ModalHeader, PageHeader, Pill, Stat, cellStyle, thStyle } from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { ntd } from '@/lib/format';

interface MonthPoint { month: string; appointments: number; revenueCents: number }
interface TenantDetail {
  tenant: { id: string; brand: string };
  treatmentMix: { name: string; count: number }[];
  trend: MonthPoint[];
}

interface TenantStat {
  tenantId: string;
  brand: string;
  customers: number;
  appointments: number;
  revenueCents: number;
  revPerCustomer: number;
  completionRate: number;
  noShowRate: number;
  recent30: number;
}
interface Analytics {
  network: {
    tenants: number;
    totalCustomers: number;
    totalAppointments: number;
    totalRevenueCents: number;
    sampleSize: number;
    minSample: number;
    reliable: boolean;
    medianRevPerCustomer: number;
    medianCompletionRate: number;
    medianNoShowRate: number;
    benchmarks: {
      revPerCustomer: { p25: number; p50: number; p75: number };
      completionRate: { p25: number; p50: number; p75: number };
      noShowRate: { p25: number; p50: number; p75: number };
    };
  };
  tenants: TenantStat[];
  treatmentTrends: { name: string; count: number }[];
  pricing: { category: string; samples: number; minCents: number; medianCents: number; maxCents: number }[];
  demographics: {
    poolSize: number;
    suppressed: boolean;
    gender: Record<string, number>;
    age: Record<string, number>;
    skinType: Record<string, number>;
    topConcerns: { name: string; count: number }[];
  };
  atRisk: { tenantId: string; brand: string; customers: number; reason: string }[];
  trends: MonthPoint[];
}

function TrendChart({ data }: { data: MonthPoint[] }) {
  const maxA = Math.max(1, ...data.map((d) => d.appointments));
  const maxR = Math.max(1, ...data.map((d) => d.revenueCents));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 160, paddingTop: 8 }}>
      {data.map((d) => (
        <div key={d.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0 }} title={`${d.month}｜預約 ${d.appointments}｜營收 ${ntd(d.revenueCents)}`}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 2, width: '100%', justifyContent: 'center' }}>
            <div style={{ width: 8, height: `${(d.appointments / maxA) * 100}%`, background: 'var(--primary)', borderRadius: 2, minHeight: d.appointments ? 2 : 0 }} />
            <div style={{ width: 8, height: `${(d.revenueCents / maxR) * 100}%`, background: 'var(--wallet)', borderRadius: 2, minHeight: d.revenueCents ? 2 : 0 }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--ink-4)', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{d.month.slice(2)}</span>
        </div>
      ))}
    </div>
  );
}

const cmp = (v: number, median: number, higherBetter: boolean) => {
  if (median === 0) return null;
  const good = higherBetter ? v >= median : v <= median;
  return good ? { bg: 'var(--success-bg)', color: 'var(--success)', t: '優於中位' } : { bg: 'var(--warn-bg)', color: 'var(--warn)', t: '低於中位' };
};

function Bars({ data, color = 'var(--primary)' }: { data: { name: string; count: number }[]; color?: string }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {data.map((d) => (
        <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 120, fontSize: 11.5, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
          <div style={{ flex: 1, height: 8, background: 'var(--surface-2)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ width: `${(d.count / max) * 100}%`, height: '100%', background: color }} />
          </div>
          <span style={{ width: 30, textAlign: 'right', fontSize: 12, fontWeight: 600 }} className="num">{d.count}</span>
        </div>
      ))}
      {data.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>尚無資料</div>}
    </div>
  );
}

function DistroChips({ obj }: { obj: Record<string, number> }) {
  const entries = Object.entries(obj);
  if (entries.length === 0) return <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {entries.map(([k, v]) => <Pill key={k} bg="var(--surface-2)" color="var(--ink-2)">{k} · {v}</Pill>)}
    </div>
  );
}

export function PlatformPage() {
  const q = useQuery({ queryKey: ['platform-analytics'], queryFn: () => api.get<Analytics>('/platform/analytics') });
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['tenant-detail', detailId],
    enabled: !!detailId,
    queryFn: () => api.get<TenantDetail>(`/platform/analytics/tenant/${detailId}`),
  });
  const d = q.data;

  if (!d) return <div style={{ padding: 24, color: 'var(--ink-3)' }}>載入分析中…</div>;
  const n = d.network;

  return (
    <div>
      <PageHeader title="大數據中台" subtitle="全網去識別化分析 · 同業標竿 · 療程趨勢 · 客群輪廓(k-匿名 + data_pool 同意)" />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="租戶數" value={String(n.tenants)} tone="primary" />
        <Stat label="全網顧客" value={String(n.totalCustomers)} tone="ink" />
        <Stat label="全網預約" value={String(n.totalAppointments)} tone="ink" />
        <Stat label="全網營收" value={ntd(n.totalRevenueCents)} tone="wallet" />
        <Stat label="客單中位數" value={ntd(n.medianRevPerCustomer)} hint="rev / customer" />
      </div>

      <Card title="近 12 個月趨勢(全網)" icon={<Icon d={I.trend} size={14} style={{ color: 'var(--primary)' }} />} right={<span style={{ fontSize: 11, color: 'var(--ink-3)' }}>■ 預約　<span style={{ color: 'var(--wallet)' }}>■</span> 營收</span>}>
        <TrendChart data={d.trends} />
      </Card>

      <Card title="同業標竿(點租戶看明細)" icon={<Icon d={I.chart} size={14} style={{ color: 'var(--primary)' }} />} right={<span style={{ fontSize: 11, color: n.reliable ? 'var(--success)' : 'var(--warn)' }}>樣本 {n.sampleSize} 家{n.reliable ? '' : `(需 ≥${n.minSample})`}</span>}>
        {!n.reliable && (
          <div style={{ padding: 10, marginBottom: 12, background: 'var(--warn-bg)', borderRadius: 8, fontSize: 12, color: 'var(--warn)' }}>
            樣本數不足(n={n.sampleSize} &lt; {n.minSample})—— 中位數/百分位僅供參考,<b>暫不做「優於/低於」評比</b>,避免小樣本誤導。
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>品牌</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>顧客</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>預約</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>營收</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>客單</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>完成率</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>爽約率</th>
            </tr>
          </thead>
          <tbody>
            {d.tenants.map((t) => {
              const rc = n.reliable ? cmp(t.revPerCustomer, n.medianRevPerCustomer, true) : null;
              const cc = n.reliable ? cmp(t.completionRate, n.medianCompletionRate, true) : null;
              const nc = n.reliable ? cmp(t.noShowRate, n.medianNoShowRate, false) : null;
              return (
                <tr key={t.tenantId} style={{ cursor: 'pointer' }} onClick={() => setDetailId(t.tenantId)}>
                  <td style={{ ...cellStyle, fontWeight: 600, color: 'var(--primary-deep)' }}>{t.brand} ›</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.customers}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.appointments}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{ntd(t.revenueCents)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{ntd(t.revPerCustomer)} {rc && <Pill bg={rc.bg} color={rc.color}>{rc.t}</Pill>}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{t.completionRate}% {cc && <Pill bg={cc.bg} color={cc.color}>{cc.t}</Pill>}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{t.noShowRate}% {nc && <Pill bg={nc.bg} color={nc.color}>{nc.t}</Pill>}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--surface-2)' }}>
              <td style={{ ...cellStyle, fontWeight: 700 }}>中位數<span style={{ fontWeight: 400, color: 'var(--ink-4)', fontSize: 10 }}> (p25–p75)</span></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={{ ...cellStyle, textAlign: 'right' }}><b>{ntd(n.benchmarks.revPerCustomer.p50)}</b><div style={{ fontSize: 10, color: 'var(--ink-4)' }}>{ntd(n.benchmarks.revPerCustomer.p25)}–{ntd(n.benchmarks.revPerCustomer.p75)}</div></td>
              <td style={{ ...cellStyle, textAlign: 'right' }}><b>{n.benchmarks.completionRate.p50}%</b><div style={{ fontSize: 10, color: 'var(--ink-4)' }}>{n.benchmarks.completionRate.p25}–{n.benchmarks.completionRate.p75}%</div></td>
              <td style={{ ...cellStyle, textAlign: 'right' }}><b>{n.benchmarks.noShowRate.p50}%</b><div style={{ fontSize: 10, color: 'var(--ink-4)' }}>{n.benchmarks.noShowRate.p25}–{n.benchmarks.noShowRate.p75}%</div></td>
            </tr>
          </tfoot>
        </table>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="全網療程需求排行" icon={<Icon d={I.trend} size={14} style={{ color: 'var(--primary)' }} />}>
          <Bars data={d.treatmentTrends} />
        </Card>

        <Card title="定價標竿(各分類)" icon={<Icon d={I.wallet} size={14} style={{ color: 'var(--primary)' }} />}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>分類</th><th style={{ ...thStyle, textAlign: 'right' }}>樣本</th><th style={{ ...thStyle, textAlign: 'right' }}>最低</th><th style={{ ...thStyle, textAlign: 'right' }}>中位</th><th style={{ ...thStyle, textAlign: 'right' }}>最高</th></tr></thead>
            <tbody>
              {d.pricing.map((p) => (
                <tr key={p.category}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{p.category}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{p.samples}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{ntd(p.minCents)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }} className="num">{ntd(p.medianCents)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{ntd(p.maxCents)}</td>
                </tr>
              ))}
              {d.pricing.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={5}>尚無資料</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      <Card
        title="客群輪廓(去識別化)"
        icon={<Icon d={I.users} size={14} style={{ color: 'var(--primary)' }} />}
        right={<span style={{ fontSize: 11, color: 'var(--ink-3)' }}>樣本池 {d.demographics.poolSize} 人 · 僅含同意 data_pool</span>}
      >
        {d.demographics.suppressed ? (
          <div style={{ padding: 12, background: 'var(--warn-bg)', borderRadius: 8, fontSize: 12.5, color: 'var(--warn)' }}>
            樣本不足或無顧客同意「資料池(data_pool)」—— 依 k-匿名原則暫不顯示客群分布。需顧客於同意書勾選 data_pool 後始納入。
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>性別</div><DistroChips obj={d.demographics.gender} /></div>
            <div><div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>年齡層</div><DistroChips obj={d.demographics.age} /></div>
            <div><div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>膚質</div><DistroChips obj={d.demographics.skinType} /></div>
            <div><div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>熱門訴求</div><Bars data={d.demographics.topConcerns} color="var(--primary-soft)" /></div>
          </div>
        )}
      </Card>

      <Card title="流失預警" icon={<Icon d={I.alert} size={14} style={{ color: 'var(--danger)' }} />}>
        {d.atRisk.length === 0 ? (
          <div style={{ padding: 12, color: 'var(--ink-4)', fontSize: 13 }}>所有租戶近 30 天皆有預約活動 👍</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {d.atRisk.map((t) => (
              <div key={t.tenantId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', background: 'var(--danger-bg)', borderRadius: 8 }}>
                <Pill bg="var(--danger-bg)" color="var(--danger)">風險</Pill>
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>{t.brand}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{t.reason}(顧客 {t.customers}）</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal open={!!detailId} onClose={() => setDetailId(null)} width={640}>
        <ModalHeader title={detail.data ? `${detail.data.tenant.brand}｜租戶明細` : '租戶明細'} sub="療程結構 + 近 12 個月趨勢" onClose={() => setDetailId(null)} />
        <div style={{ padding: 20 }}>
          {!detail.data ? (
            <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>載入中…</div>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>近 12 個月趨勢</div>
              <TrendChart data={detail.data.trend} />
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', margin: '16px 0 8px' }}>療程結構(前 10)</div>
              <Bars data={detail.data.treatmentMix} />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
